import React, { useCallback, useRef } from "react";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { Box, CircularProgress } from "@mui/material";
import { dt } from "../../../../shared/themes/redesignTokens";
import { GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_SCRIPT_ID } from "../../../../shared/config/googleMapsConfig";

// Clean map style: reduce POI noise
const defaultMapStyles: google.maps.MapTypeStyle[] = [
  {
    featureType: "poi",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.business",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "transit",
    elementType: "labels.icon",
    stylers: [{ visibility: "off" }],
  },
];

const defaultMapOptions: google.maps.MapOptions = {
  styles: defaultMapStyles,
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  gestureHandling: "greedy",
};

const containerStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
};

interface BaseMapContainerProps {
  children?: React.ReactNode;
  center?: { lat: number; lng: number };
  zoom?: number;
  mapOptions?: Partial<google.maps.MapOptions>;
  onLoad?: (map: google.maps.Map) => void;
  onZoomChanged?: (zoom: number) => void;
  loadingText?: string;
}

const BaseMapContainer: React.FC<BaseMapContainerProps> = ({
  children,
  center = { lat: 49.8, lng: 15.5 },
  zoom = 7,
  mapOptions,
  onLoad: onLoadProp,
  onZoomChanged,
  loadingText = "Loading map...",
}) => {
  const { translate } = useTranslation("serviceTracking");
  const mapRef = useRef<google.maps.Map | null>(null);
  const apiKey = (window as any).app?.config?.googleMapsApiKey || "";

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    id: GOOGLE_MAPS_SCRIPT_ID,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      if (onLoadProp) onLoadProp(map);
    },
    [onLoadProp]
  );

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const handleZoomChanged = useCallback(() => {
    if (mapRef.current && onZoomChanged) {
      const newZoom = mapRef.current.getZoom();
      if (typeof newZoom === "number") {
        onZoomChanged(newZoom);
      }
    }
  }, [onZoomChanged]);

  const mergedOptions = { ...defaultMapOptions, ...mapOptions };

  if (loadError) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          fontFamily: dt.font,
          color: dt.textSecondary,
          fontSize: "14px",
        }}
      >
        {translate("mapLoadFailed")}
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          gap: 2,
        }}
      >
        <CircularProgress size={32} sx={{ color: "#10B981" }} />
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "13px",
            color: dt.textSecondary,
          }}
        >
          {loadingText}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={zoom}
        options={mergedOptions}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onZoomChanged={handleZoomChanged}
      >
        {children}
      </GoogleMap>
    </Box>
  );
};

export default React.memo(BaseMapContainer);
